import React, { useState } from 'react';
import { FaPlay, FaStop, FaPlus, FaCalendarAlt, FaBullseye } from 'react-icons/fa';
import Spinner from '../common/Spinner';

const PatientManagementList = ({
  profiles,
  healthGoals,
  loading,
  onStartGoal,
  onEndGoal,
  pagination
}) => {
  const [expandedProfileId, setExpandedProfileId] = useState(null);
  const [selectedGoalId, setSelectedGoalId] = useState({});

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!profiles || profiles.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">No profiles found</p>
      </div>
    );
  }

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-4">
      {profiles.map((profile) => {
        const isExpanded = expandedProfileId === profile.id;
        const hasActiveGoal = profile.currentGoal && profile.currentGoal.id;
        const age = calculateAge(profile.dob);

        return (
          <div
            key={profile.id}
            className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden"
          >
            {/* Main Profile Card */}
            <div
              onClick={() =>
                setExpandedProfileId(isExpanded ? null : profile.id)
              }
              className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                    {getInitials(profile.name)}
                  </div>
                </div>

                {/* Profile Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {profile.name}
                    </h3>
                    {hasActiveGoal && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                        Active Goal
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <span>📧 {profile.email || '-'}</span>
                    <span>📱 {profile.phone || '-'}</span>
                    {age && <span>👤 {age} yrs</span>}
                  </div>
                </div>

                {/* Current Goal Badge */}
                <div className="flex-shrink-0 text-right">
                  {hasActiveGoal ? (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5">
                      <p className="text-sm font-semibold text-orange-600 leading-tight">
                        {profile.currentGoal.goal_name}
                      </p>
                      <p className="text-xs text-gray-500 leading-tight">
                        <FaCalendarAlt className="inline mr-1" size={11} />
                        {formatDate(profile.currentGoal.start_at)}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
                      <p className="text-xs text-gray-600 font-medium">No Active Goal</p>
                    </div>
                  )}
                </div>

                {/* Expand Icon */}
                <div className="flex-shrink-0 text-gray-400">
                  <svg
                    className={`w-5 h-5 transition-transform ${
                      isExpanded ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 14l-7 7m0 0l-7-7m7 7V3"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="border-t border-gray-200 bg-gray-50 p-4">
                <div className="space-y-4">
                  {/* Goal Management Section */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FaBullseye className="text-orange-600" />
                      Goal Management
                    </h4>

                    {hasActiveGoal ? (
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <p className="text-sm font-medium text-green-900 mb-2">
                            Active Goal: {profile.currentGoal.goal_name}
                          </p>
                          <button
                            onClick={() => onEndGoal(profile.id)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                          >
                            <FaStop size={14} />
                            End Current Goal
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <select
                            value={selectedGoalId[profile.id] || ''}
                            onChange={(e) =>
                              setSelectedGoalId({
                                ...selectedGoalId,
                                [profile.id]: e.target.value
                              })
                            }
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          >
                            <option value="">Select a goal to start...</option>
                            {healthGoals.map((goal) => (
                              <option key={goal.id} value={goal.id}>
                                {goal.goal_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          onClick={() => {
                            const goalId = selectedGoalId[profile.id];
                            if (goalId) {
                              onStartGoal(profile.id, parseInt(goalId));
                              setSelectedGoalId({
                                ...selectedGoalId,
                                [profile.id]: ''
                              });
                            }
                          }}
                          disabled={!selectedGoalId[profile.id]}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FaPlay size={14} />
                          Start Selected Goal
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Profile Details */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      Profile Details
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 font-medium">Gender</p>
                        <p className="text-gray-900 capitalize">
                          {profile.gender || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 font-medium">Age</p>
                        <p className="text-gray-900">{age || '-'} years</p>
                      </div>
                      <div>
                        <p className="text-gray-600 font-medium">Email</p>
                        <p className="text-gray-900 truncate">
                          {profile.email || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 font-medium">Phone</p>
                        <p className="text-gray-900">{profile.phone || '-'}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-gray-600 font-medium">Date of Birth</p>
                        <p className="text-gray-900">
                          {formatDate(profile.dob)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Pagination Info */}
      {pagination && (
        <div className="flex justify-center items-center gap-2 mt-6 text-sm text-gray-600">
          <span>
            Page {pagination.page} of {pagination.totalPages} • Total:{' '}
            {pagination.total} profiles
          </span>
        </div>
      )}
    </div>
  );
};

export default PatientManagementList;
